import { useState } from 'react';
import { Button, toast } from '@kmgestao/ui';

import { isTerminalStage, type LeadStage } from '../../../features/leads/index.js';
import {
  useGenerateContract,
  type LeadProposalSummary,
} from '../../../features/proposals/index.js';

/** Bloco de ações de contrato dentro do card de proposta aceita
 * (LeadProposalsSection). Aparece SÓ quando `proposal.status = 'accepted'`.
 *
 * Comportamento por stage do lead (PR-M1-01c-iii.b — 2026-04-25):
 *   - lead em estado ativo (`contract_sent`, `proposal_accepted`):
 *     exibe CTA "Gerar contrato" (cria contract draft via
 *     create-contract → generate-contract-pdf → email contract_ready).
 *   - lead em **estado terminal** (`signed` ou `provisioned`): NÃO
 *     mostra mais o CTA — gerar contrato perdeu sentido. Exibe
 *     resumo "Contrato assinado" pra Karla saber o que aconteceu sem
 *     poder clicar errado. PDF certificado vem na PR-M1-01f.b.
 *
 * Reportado pelo Alexandre 2026-04-25: a UI mostrava "📄 Próximo passo:
 * gerar contrato" mesmo depois do lead ter assinado, induzindo Karla
 * a clicar e gerar contrato novo (que daria erro). */
export function ProposalContractActions({
  proposal,
  leadId,
  leadStage,
}: {
  proposal: LeadProposalSummary;
  leadId: string;
  leadStage: LeadStage;
}) {
  const [confirm, setConfirm] = useState(false);
  const generate = useGenerateContract();

  if (proposal.status !== 'accepted') return null;

  // Em estado terminal — lead virou cliente — nada de CTA "gerar
  // contrato" (já gerou e assinou). Resumo informativo no lugar.
  if (isTerminalStage(leadStage)) {
    return (
      <div
        className="space-y-1 rounded-md border border-accent/40 bg-accent/[0.04] p-3 text-sm"
        role="status"
      >
        <p className="font-semibold text-accent-foreground">✍️ Contrato assinado</p>
        <p className="text-xs text-muted-foreground">
          Esta proposta virou contrato e foi assinada pelo lead via Assinafy. Acompanhe o status no
          banner do topo da página.
        </p>
      </div>
    );
  }

  const handleGenerate = async () => {
    try {
      const res = await generate.mutateAsync({
        proposal_id: proposal.id,
        lead_id: leadId,
      });
      const reused = res.reused === true;
      toast.success(
        reused
          ? 'Contrato já existia — carregado.'
          : 'Contrato gerado. O lead receberá o email de revisão em instantes.',
      );
      setConfirm(false);
    } catch (err) {
      toast.error('Não foi possível gerar o contrato', {
        description: err instanceof Error ? err.message : 'erro desconhecido',
      });
    }
  };

  return (
    <div className="space-y-2 rounded-md border border-accent/30 bg-accent/5 p-3 text-sm">
      <p className="font-semibold text-foreground">📄 Próximo passo: gerar contrato</p>
      <p className="text-xs text-muted-foreground">
        Proposta foi aceita. Ao gerar, o lead recebe link pra revisar o contrato e decidir entre
        aprovar ou solicitar ajuste antes da assinatura digital.
      </p>
      <div className="flex flex-wrap gap-2 pt-1">
        {confirm ? (
          <>
            <Button size="sm" onClick={handleGenerate} disabled={generate.isPending}>
              {generate.isPending ? 'Gerando…' : 'Confirmar geração'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setConfirm(false)}
              disabled={generate.isPending}
            >
              Cancelar
            </Button>
          </>
        ) : (
          <Button size="sm" onClick={() => setConfirm(true)}>
            Gerar contrato
          </Button>
        )}
      </div>
    </div>
  );
}
